import React, { useEffect, useMemo, useState } from 'react';
import { Role, SystemAuditLogEntry } from '../types';
import { adminService } from '../services/adminService';

interface AuditLogViewProps {
  currentRole: Role;
}

const defaultFilters = {
  search: '',
  action: '',
  entity: '',
  from: '',
  to: '',
};

const AuditLogView: React.FC<AuditLogViewProps> = ({ currentRole }) => {
  const [filters, setFilters] = useState(defaultFilters);
  const [logs, setLogs] = useState<SystemAuditLogEntry[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [lastUpdated, setLastUpdated] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isExporting, setIsExporting] = useState(false);
  const [archives, setArchives] = useState<Array<{ name: string; size: number; createdAt: string }>>([]);
  const [isLoadingArchives, setIsLoadingArchives] = useState(false);
  const [verifyReport, setVerifyReport] = useState<{ ok: boolean; checked: number; issues: any[] } | null>(null);
  const [isVerifying, setIsVerifying] = useState(false);

  const loadLogs = async () => {
    setIsLoading(true);
    const response = await adminService.getAuditLogs({
      limit: 500,
      search: filters.search || undefined,
      action: filters.action || undefined,
      entity: filters.entity || undefined,
      from: filters.from || undefined,
      to: filters.to || undefined,
    });
    if (response.error) {
      setErrorMessage(response.error);
    } else {
      setErrorMessage(null);
    }
    setLogs(response.data);
    setLastUpdated(new Date().toISOString());
    setIsLoading(false);
  };

  const handleExportCsv = async () => {
    setIsExporting(true);
    const blob = await adminService.exportAuditCsv({
      limit: 20000,
      search: filters.search || undefined,
      action: filters.action || undefined,
      entity: filters.entity || undefined,
      from: filters.from || undefined,
      to: filters.to || undefined,
    });
    if (!blob) {
      setErrorMessage('CSV export failed. Please retry.');
      setIsExporting(false);
      return;
    }
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `audit-export-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
    setIsExporting(false);
  };

  const handleDownloadArchive = async (name: string) => {
    const blob = await adminService.downloadAuditArchive(name);
    if (!blob) {
      setErrorMessage('Archive download failed. Please retry.');
      return;
    }
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = name;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  };

  const handleVerifyChain = async () => {
    setIsVerifying(true);
    const report = await adminService.verifyAuditChain(20000);
    setVerifyReport(report);
    setIsVerifying(false);
  };

  useEffect(() => {
    const handle = setTimeout(() => {
      loadLogs();
    }, 300);
    return () => clearTimeout(handle);
  }, [filters]);

  useEffect(() => {
    const loadArchives = async () => {
      setIsLoadingArchives(true);
      const data = await adminService.getAuditArchives();
      setArchives(data);
      setIsLoadingArchives(false);
    };
    loadArchives();
  }, []);

  const actionCounts = useMemo(() => {
    const counts = new Map<string, number>();
    logs.forEach(log => {
      counts.set(log.action, (counts.get(log.action) || 0) + 1);
    });
    return Array.from(counts.entries()).sort((a, b) => b[1] - a[1]);
  }, [logs]);

  const entityCounts = useMemo(() => {
    const counts = new Map<string, number>();
    logs.forEach(log => {
      counts.set(log.entity, (counts.get(log.entity) || 0) + 1);
    });
    return Array.from(counts.entries()).sort((a, b) => b[1] - a[1]);
  }, [logs]);

  const userCounts = useMemo(() => {
    const counts = new Map<string, number>();
    logs.forEach(log => {
      const key = log.userEmail || log.userId || 'unknown';
      counts.set(key, (counts.get(key) || 0) + 1);
    });
    return Array.from(counts.entries()).sort((a, b) => b[1] - a[1]);
  }, [logs]);

  const actionOptions = useMemo(() => {
    const set = new Set(logs.map(log => log.action));
    return Array.from(set).sort();
  }, [logs]);

  const entityOptions = useMemo(() => {
    const set = new Set(logs.map(log => log.entity));
    return Array.from(set).sort();
  }, [logs]);

  const formatDate = (value: string) => new Date(value).toLocaleString('en-GB');

  if (currentRole !== 'ADMIN' && currentRole !== 'SUPER_ADMIN' && currentRole !== 'CEO') {
    return (
      <div className="max-w-4xl mx-auto bg-white border border-slate-100 rounded-3xl p-10 shadow-sm">
        <p className="text-[10px] font-black text-rose-500 uppercase tracking-[0.3em]">Access Restricted</p>
        <p className="mt-4 text-sm font-semibold text-slate-700">
          Audit Ledger is available to ADMIN, SUPER_ADMIN, and CEO roles.
        </p>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto space-y-10 animate-in fade-in duration-500 pb-20">
      <div className="flex items-end justify-between border-b border-slate-100 pb-6">
        <div>
          <h2 className="text-3xl font-black text-slate-900 tracking-tight">Audit Ledger</h2>
          <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mt-2">
            Source of Truth — Every Administrative Action
          </p>
        </div>
        <div className="flex items-center gap-4">
          <div className="text-right">
            <p className="text-[9px] font-black uppercase tracking-[0.25em] text-slate-400">Last Sync</p>
            <p className="text-xs font-mono text-slate-700">
              {lastUpdated ? formatDate(lastUpdated) : '—'}
            </p>
          </div>
          <button
            onClick={handleExportCsv}
            className="px-5 py-3 rounded-2xl text-[10px] font-black uppercase tracking-widest bg-slate-900 text-white hover:bg-slate-800 transition-all"
          >
            {isExporting ? 'Exporting…' : 'Export CSV'}
          </button>
          <button
            onClick={handleVerifyChain}
            className="px-5 py-3 rounded-2xl text-[10px] font-black uppercase tracking-widest bg-slate-100 text-slate-600 hover:bg-slate-200 transition-all"
          >
            {isVerifying ? 'Verifying…' : 'Verify Chain'}
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[1.2fr_1fr] gap-6">
        <div className="bg-white border border-slate-100 rounded-[2.5rem] p-8 shadow-sm">
          {errorMessage && (
            <div className="mb-6 rounded-2xl border border-rose-100 bg-rose-50 px-4 py-3 text-[10px] font-black uppercase tracking-widest text-rose-600">
              {errorMessage}
            </div>
          )}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="space-y-2">
              <label className="text-[9px] font-black text-slate-400 uppercase tracking-widest">Search</label>
              <input
                value={filters.search}
                onChange={e => setFilters(prev => ({ ...prev, search: e.target.value }))}
                className="w-full bg-slate-50 border border-slate-200 rounded-2xl px-4 py-3 text-xs font-semibold text-slate-700 outline-none focus:ring-2 focus:ring-indigo-200"
                placeholder="User, action, entity"
              />
            </div>
            <div className="space-y-2">
              <label className="text-[9px] font-black text-slate-400 uppercase tracking-widest">Action</label>
              <select
                value={filters.action}
                onChange={e => setFilters(prev => ({ ...prev, action: e.target.value }))}
                className="w-full bg-slate-50 border border-slate-200 rounded-2xl px-4 py-3 text-xs font-semibold text-slate-700 outline-none"
              >
                <option value="">All Actions</option>
                {actionOptions.map(action => (
                  <option key={action} value={action}>{action}</option>
                ))}
              </select>
            </div>
            <div className="space-y-2">
              <label className="text-[9px] font-black text-slate-400 uppercase tracking-widest">Entity</label>
              <select
                value={filters.entity}
                onChange={e => setFilters(prev => ({ ...prev, entity: e.target.value }))}
                className="w-full bg-slate-50 border border-slate-200 rounded-2xl px-4 py-3 text-xs font-semibold text-slate-700 outline-none"
              >
                <option value="">All Entities</option>
                {entityOptions.map(entity => (
                  <option key={entity} value={entity}>{entity}</option>
                ))}
              </select>
            </div>
            <div className="space-y-2">
              <label className="text-[9px] font-black text-slate-400 uppercase tracking-widest">From</label>
              <input
                type="date"
                value={filters.from}
                onChange={e => setFilters(prev => ({ ...prev, from: e.target.value }))}
                className="w-full bg-slate-50 border border-slate-200 rounded-2xl px-4 py-3 text-xs font-semibold text-slate-700 outline-none"
              />
            </div>
            <div className="space-y-2">
              <label className="text-[9px] font-black text-slate-400 uppercase tracking-widest">To</label>
              <input
                type="date"
                value={filters.to}
                onChange={e => setFilters(prev => ({ ...prev, to: e.target.value }))}
                className="w-full bg-slate-50 border border-slate-200 rounded-2xl px-4 py-3 text-xs font-semibold text-slate-700 outline-none"
              />
            </div>
            <div className="flex items-end gap-3">
              <button
                onClick={() => setFilters(defaultFilters)}
                className="px-4 py-3 rounded-2xl text-[10px] font-black uppercase tracking-widest bg-slate-100 text-slate-500 hover:bg-slate-200 transition-all"
              >
                Reset
              </button>
              <button
                onClick={loadLogs}
                className="px-5 py-3 rounded-2xl text-[10px] font-black uppercase tracking-widest bg-indigo-600 text-white shadow-lg shadow-indigo-100 hover:bg-indigo-500 transition-all"
              >
                Refresh
              </button>
            </div>
          </div>
        </div>

        <div className="bg-slate-900 rounded-[2.5rem] p-8 text-white shadow-xl">
          <div className="flex items-center justify-between">
            <p className="text-[10px] font-black uppercase tracking-[0.3em] text-slate-400">Total Events</p>
            {isLoading && <span className="text-[10px] font-black text-indigo-300">Syncing…</span>}
          </div>
          <p className="text-4xl font-black font-mono mt-4">{logs.length.toLocaleString()}</p>
          <div className="mt-6 space-y-4">
            {actionCounts.slice(0, 4).map(([action, count]) => (
              <div key={action} className="space-y-2">
                <div className="flex justify-between text-[10px] font-bold uppercase tracking-widest text-slate-400">
                  <span className="truncate max-w-[220px]">{action}</span>
                  <span className="font-mono text-slate-200">{count}</span>
                </div>
                <div className="h-2 bg-white/10 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-indigo-400"
                    style={{ width: `${Math.min(100, (count / (actionCounts[0]?.[1] || 1)) * 100)}%` }}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="bg-white border border-slate-100 rounded-[2.5rem] p-8 shadow-sm">
          <p className="text-[10px] font-black uppercase tracking-[0.3em] text-slate-400">Top Entities</p>
          <div className="mt-6 space-y-4">
            {entityCounts.slice(0, 5).map(([entity, count]) => (
              <div key={entity} className="flex items-center justify-between text-xs font-semibold text-slate-700">
                <span className="truncate">{entity}</span>
                <span className="font-mono text-slate-500">{count}</span>
              </div>
            ))}
            {!entityCounts.length && (
              <p className="text-xs text-slate-400">No entities recorded.</p>
            )}
          </div>
        </div>
        <div className="bg-white border border-slate-100 rounded-[2.5rem] p-8 shadow-sm">
          <p className="text-[10px] font-black uppercase tracking-[0.3em] text-slate-400">Top Actors</p>
          <div className="mt-6 space-y-4">
            {userCounts.slice(0, 5).map(([user, count]) => (
              <div key={user} className="flex items-center justify-between text-xs font-semibold text-slate-700">
                <span className="truncate">{user}</span>
                <span className="font-mono text-slate-500">{count}</span>
              </div>
            ))}
            {!userCounts.length && (
              <p className="text-xs text-slate-400">No actors recorded.</p>
            )}
          </div>
        </div>
        <div className="bg-white border border-slate-100 rounded-[2.5rem] p-8 shadow-sm">
          <p className="text-[10px] font-black uppercase tracking-[0.3em] text-slate-400">Operational Notes</p>
          <ul className="mt-6 text-xs text-slate-500 space-y-3">
            <li>All HTTP requests are recorded (including login and reads).</li>
            <li>Entries include request metadata and response status.</li>
            <li>Use filters to isolate incidents or compliance checks.</li>
          </ul>
          {verifyReport && (
            <div className={`mt-6 rounded-2xl px-4 py-3 text-[10px] font-black uppercase tracking-widest ${
              verifyReport.ok ? 'bg-emerald-50 text-emerald-600' : 'bg-rose-50 text-rose-600'
            }`}>
              {verifyReport.ok
                ? `Hash chain verified (${verifyReport.checked} events)`
                : `Integrity issues detected (${verifyReport.issues.length})`}
            </div>
          )}
        </div>
      </div>

      <div className="bg-white border border-slate-100 rounded-[2.5rem] p-8 shadow-sm">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-lg font-black text-slate-900">Audit Archives</h3>
            <p className="text-[9px] font-black uppercase tracking-[0.25em] text-slate-400">Daily CSV Snapshots</p>
          </div>
          {isLoadingArchives && (
            <span className="text-[10px] font-bold text-slate-400">Loading…</span>
          )}
        </div>
        <div className="space-y-3">
          {archives.map(archive => (
            <div key={archive.name} className="flex items-center justify-between text-xs font-semibold text-slate-700">
              <div>
                <p className="font-mono text-slate-600">{archive.name}</p>
                <p className="text-[10px] text-slate-400">
                  {new Date(archive.createdAt).toLocaleString('en-GB')} · {(archive.size / 1024).toFixed(1)} KB
                </p>
              </div>
              <button
                onClick={() => handleDownloadArchive(archive.name)}
                className="px-4 py-2 rounded-xl text-[10px] font-black uppercase tracking-widest bg-slate-900 text-white hover:bg-slate-800 transition-all"
              >
                Download
              </button>
            </div>
          ))}
          {archives.length === 0 && !isLoadingArchives && (
            <p className="text-xs text-slate-400">No archives created yet.</p>
          )}
        </div>
      </div>

      <div className="bg-white border border-slate-100 rounded-[2.5rem] p-8 shadow-sm">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h3 className="text-lg font-black text-slate-900">Audit Timeline</h3>
            <p className="text-[9px] font-black uppercase tracking-[0.25em] text-slate-400">Latest 500 Events</p>
          </div>
          {isLoading && <span className="text-xs font-semibold text-slate-400">Loading…</span>}
        </div>
        <div className="space-y-4 max-h-[520px] overflow-y-auto pr-2 custom-scrollbar">
          {logs.map(log => (
            <div key={log.id} className="flex items-start justify-between gap-6 border-b border-slate-50 pb-4">
              <div className="space-y-2">
                <div className="flex items-center gap-3">
                  <span className="text-[9px] font-black uppercase tracking-widest text-indigo-500">
                    {log.action}
                  </span>
                  <span className="text-[9px] font-black uppercase tracking-widest text-slate-400">
                    {log.entity}{log.entityId ? ` • ${log.entityId}` : ''}
                  </span>
                </div>
                <p className="text-xs text-slate-600">
                  {log.userEmail || log.userId || 'unknown'} · {log.ipAddress || 'IP n/a'} · {log.userAgent || 'UA n/a'}
                </p>
              </div>
              <div className="text-right text-[10px] font-black uppercase tracking-widest text-slate-400">
                {formatDate(log.timestamp)}
              </div>
            </div>
          ))}
          {logs.length === 0 && !isLoading && (
            <div className="py-10 text-center text-xs font-semibold text-slate-400">
              No audit events found for the selected filters.
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AuditLogView;
